<template>
  <header class="navbar">
    <div class="navbar-container">
      <div class="navbar-brand">
        <img src="@/assets/logo.png" alt="Logo" class="logo" />
        <span class="company-name">匠心致诚</span>
      </div>

      <nav class="desktop-nav">
        <ul>
          <li v-for="(item, idx) in navItems" :key="item.name">
            <a href="#" @click.prevent="scrollToSection(idx, item.name)" class="nav-link" :class="{ active: activeIdx === idx }">
              {{ item.label }}
            </a>
          </li>
        </ul>
      </nav>

      <div class="desktop-contact">
        <span class="contact-icon">☎</span>
        <a class="contact-phone" href="tel:15927220112">159-2722-0112</a>
      </div>

      <div class="mobile-contact">
        <a href="tel:15927220112" class="mobile-phone-link">📞</a>
      </div>
    </div>
  </header>
</template>

<script>
export default {
  name: 'NavBar',
  data() {
    return {
      // 导航菜单项配置：label为显示文字，name为对应的页面锚点ID
      navItems: [
        { label: '公司简介', name: 'about' },
        { label: '核心优势', name: 'advantages' },
        { label: '核心业务', name: 'business' },
        { label: '客户定制', name: 'service' },
        { label: '服务能力', name: 'capability' },
        { label: '团队介绍', name: 'team' },
        { label: '联系我们', name: 'contact' }
      ],
      activeIdx: 0,           // 当前高亮的菜单项索引
      sectionOffsets: [],     // 存储各区块相对于页面顶部的偏移量
      scrollTimeout: null     // 滚动节流定时器，用于优化性能
    };
  },
  mounted() {
    // 监听滚动事件，用于更新导航高亮
    window.addEventListener('scroll', this.handleScroll);
    // 监听窗口大小变化，重新计算各区块偏移量
    window.addEventListener('resize', this.handleResize);
    // 等待DOM渲染完成后计算各区块位置
    this.$nextTick(this.calcSectionOffsets);
  },
  beforeUnmount() {
    // 组件销毁前移除事件监听，避免内存泄漏
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    /**
     * 滚动到指定区块
     * @param {number} idx - 菜单项索引
     * @param {string} name - 区块的ID
     */
    scrollToSection(idx, name) {
      // 更新当前激活的菜单项
      this.activeIdx = idx;
      
      // 获取目标DOM元素
      const element = document.getElementById(name);
      if (element) {
        // 获取导航栏高度（80px），用于计算滚动偏移
        const navbarHeight = 80;
        // 计算目标元素相对于视口顶部的位置
        const elementPosition = element.getBoundingClientRect().top;
        // 计算需要滚动到的绝对位置（减去导航栏高度，避免被遮挡）
        const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

        // 平滑滚动到目标位置
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    },
    
    /**
     * 处理滚动事件：根据当前滚动位置高亮对应的导航菜单项
     * 使用节流优化性能，避免频繁触发
     */
    handleScroll() {
      if (this.scrollTimeout) return;
      
      this.scrollTimeout = setTimeout(() => {
        this.scrollTimeout = null;
        
        // 获取导航栏高度，用于计算当前所在的区块
        const navbarHeight = 80;
        // 当前滚动位置 + 导航栏高度 + 缓冲值（50px），确定用户当前看到的区块
        const scrollY = window.scrollY + navbarHeight + 50;
        
        // 倒序遍历区块偏移量，找到当前所在的区块
        for (let i = this.sectionOffsets.length - 1; i >= 0; i--) {
          if (scrollY >= this.sectionOffsets[i].offset) {
            if (this.activeIdx !== i) {
              this.activeIdx = i;
            }
            break;
          }
        }
      }, 10); // 10ms节流，保证滚动流畅
    },
    
    /**
     * 窗口大小变化时重新计算各区块偏移量
     * 因为元素位置可能因窗口缩放而改变
     */
    handleResize() {
      this.$nextTick(this.calcSectionOffsets);
    },
    
    /**
     * 计算各区块相对于页面顶部的偏移量
     * 用于滚动时判断当前应该高亮哪个导航菜单项
     */
    calcSectionOffsets() {
      this.sectionOffsets = this.navItems.map((item) => {
        const element = document.getElementById(item.name);
        return { 
          name: item.name, 
          offset: element ? element.offsetTop : 0 
        };
      });
    }
  }
};
</script>

<style scoped>
/* ============================================================
   导航栏整体样式
   ============================================================ */
/* 固定在页面顶部，背景为白色不透明，导航栏不随页面滚动而移动 */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: #ffffff;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  z-index: 1000;
}

/* 导航栏内容容器：电脑端最大宽度1200px，水平居中 */
.navbar-container {
  max-width: 1200px;
  height: 80px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* ============================================================
   左侧 Logo 区域样式
   ============================================================ */
.navbar-brand {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 0 0 0 20px;
}

/* Logo图片样式：固定尺寸，保持圆形 */
.navbar-brand .logo {
  width: 42px;
  height: 42px;
  /* 给白色 Logo 添加浅色投影，使其在白色背景上可见 */
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15));
}

/* 公司名称样式：黑色加粗，略大 */
.company-name {
  font-size: 1.8rem;
  font-weight: bold;
  color: #1a1a1a;
  letter-spacing: 1px;
}

/* ============================================================
   中间导航菜单样式（电脑端）
   ============================================================ */
.desktop-nav {
  display: flex;
  padding: 8px 12px;
}

.desktop-nav ul {
  display: flex;
  list-style: none;
  gap: 50px;
  margin: 0;
  padding: 0;
}

.desktop-nav li {
  list-style: none;
}

/* 导航链接样式：灰色文字，悬停和高亮时变为品牌蓝色 */
.nav-link {
  font-size: 1.1rem;
  font-weight: 400;
  color: #2f2f2f;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;
}

.nav-link:hover,
.nav-link.active {
  color: #1976d2;
}

/* ============================================================
   电脑端-联系方式样式
   ============================================================ */
.desktop-contact {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 30px;
  transition: background 0.2s ease;
}

/* 联系方式悬停效果：浅灰色背景，增强交互反馈 */
.desktop-contact:hover {
  background: #f0f2f5;
}

/* 电话图标样式 */
.contact-icon {
  font-size: 1.2rem;
  margin-right: 6px;
}

/* 电话号码文字样式 */
.contact-phone {
  font-size: 1.1rem;
  color: #2f2f2f;
  font-weight: 500;
  text-decoration: none;
}

/* ============================================================
   移动端-联系方式样式
   ============================================================ */
.mobile-contact {
  display: none;
}

.mobile-phone-link {
  font-size: 1.4rem;
  text-decoration: none;
  color: #1976d2;
}

/* ============================================================
   响应式样式：平板电脑（宽度小于1024px）
   ============================================================ */
@media (max-width: 1024px) {
  .navbar-container {
    padding: 0 20px;
  }
  
  .desktop-nav ul {
    gap: 20px;
  }
  
  .company-name {
    font-size: 1.1rem;
  }
}

/* ============================================================
   响应式样式：手机端（宽度小于768px）
   ============================================================ */
@media (max-width: 768px) {
  .navbar-container {
    height: 60px;
    padding: 0 16px;
  }
  
  /* 隐藏电脑端导航菜单 */
  .desktop-nav {
    display: none;
  }
  
  /* 隐藏电脑端联系方式区域 */
  .desktop-contact {
    display: none;
  }
  
  /* 显示移动端电话图标 */
  .mobile-contact {
    display: block;
  }
  
  .logo {
    width: 36px;
    height: 36px;
  }
  
  .company-name {
    font-size: 1rem;
  }
}
</style>